/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactElement} from 'react';
import {useThemeContext} from './useThemeContext';

/**
 * Props for ThemedComponent.
 */
export interface ThemedComponentProps {
  /**
   * Component to render when theme is Themes.legacy.
   */
  legacyComponent: ReactElement;

  /**
   * Component to render when theme is Themes.current.
   */
  currentComponent: ReactElement;
}

/**
 * Conditionally renders different components based on the currently
 * selected theme.
 *
 * Useful for cases where the theme mandates an entirely different UX
 * or significant changes outside of styling and layout concerns.
 *
 * ```typescript
 * import ThemedComponent from '@ion/dls/src/theme/ThemedComponent';
 *
 * const SomeComponent = () => (
 *   <ThemedComponent legacyComponent={<LegacyUI />} currentComponent={<ModernUI />}
 *   />
 * );
 * ```
 *
 * @param   legacyComponent Component to render for legacy theme.
 * @param   currentComponent Component to render for current theme.
 * @returns The appropriate component based on current theme.
 */
export const ThemedComponent = ({
  legacyComponent,
  currentComponent
}: ThemedComponentProps) => {
  const {isCurrent} = useThemeContext();
  return isCurrent ? currentComponent : legacyComponent;
};
export default ThemedComponent;
